import {createStore, emitOnce, filterNil} from '@ngneat/elf';
import {getEntityByPredicate, selectEntity, selectManyByPredicate, updateEntities, withEntities} from "@ngneat/elf-entities";
import {Injectable} from "@angular/core";
import {erstatteAlleEntiteterMedForelder, FeilhåndteringArgumenter, Feilhåndteringsstrategi, FeilhåndteringsstrategiForDataOgMeta, finnFraLokallagerBasertPåForelderId, forelderIdPredikat, hentFraLokallager, hentFraLokallagerEllerNull, håndterRequestfeilViaCatchError, leggTilForelderId, Leveringsvalg, oppdaterEntitetMedForelder, registrerEntiteterMedForelder, registrerEntitetMedForelder, serverFraLagerOgRequestPakketIMeta, serverFraRequest, serverFraRequestPakketIMeta, slettAlleEntiteterMedForelder, slettRekursivt, somFeilhåndteringsstrategiForDataOgMeta, stdFeilhåndteringsstrategi, stdFeilhåndteringsstrategiForDataOgMeta, STRATEGI_TYPE, visFeilmeldingVhjaMeldingTilBruker} from "felles-ng2/tjenester/datalager/datatjeneste";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {MeldingTilBrukerNg2} from "felles-ng2/tjenester/MeldingTilBrukerNg2";
import {i18n, Oversettelser} from "felles-ng2/tjenester/i18n/Oversettelser";
import {ArkivFilnodeModell, ArkivMappenodeModell, ArkivnodeId, ArkivNodeModell, ArkivRessursClient, DokumentId, DokumentVersjonModell, FavorittType, GruppeId, ImportresultatModell, Modelloppdatering, ModelloppdateringType, NyMappeSpesifikasjon, OppdateringmodellArkivnode, OppdateringmodellArkivnodeSlettet, OppdateringmodellDokument, OppdateringmodellDokumentversjon, OppdaterMappeSpesifikasjon, SkjermingDetaljerModell, SkjermingModell, TynnFavorittModell} from "../ressurser/generertNg2HttpKlient";
import {DatalagerAbonnenter} from "felles-ng2/tjenester/datalager/DatalagerAbonnenter";
import {firstValueFrom, map, MonoTypeOperatorFunction, Observable, tap} from "rxjs";
import {HttpEvent, HttpEventType, HttpResponse} from "@angular/common/http";
import {ModellOpprettetEllerOppdatertHendelseHåndteringBygger} from "felles-ng2/tjenester/datalager/modellHendelseHåndtering/ModellOpprettetEllerOppdatertHendelseHåndteringBygger";
import {ModellSlettetHendelseHåndteringBygger} from "felles-ng2/tjenester/datalager/modellHendelseHåndtering/ModellSlettetHendelseHåndteringBygger";
import {sorterLevertSamling, utelatFørsteDersomNil} from "felles/ng2/rxjsHjelpere";
import {i18nArgumenterMedAnsvarligBruker} from "felles-ng2/modell/modelloppdateringModellHjelpere";
import {erFilNode, erMappeNode, sorterArkivnode} from "../../modell/arkivModellHjelpere";
import {DataOgMeta, mapTilData} from "felles-ng2/tjenester/datalager/DataOgMeta";
import {distinctUntilChangedDeep} from "felles-ng2/støttefunksjoner/observable";
import {formDataMedFil} from "felles-ng2/støttefunksjoner/dom";
import {inneholderEntitetBasertPåId} from "felles-ng2/modell/modellHjelpere";
import {erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {verifiserErSann} from "felles-ng2/støttefunksjoner/antakelser";
import {Modelloppdateringer} from "felles-ng2/tjenester/Modelloppdateringer";
import {FavorittDatatjeneste} from "felles-ng2/tjenester/datalager/FavorittDatatjeneste";

const debug = initDebugger('ArkivDatatjeneste');


/** Datalager for {@link ArkivNodeModell}.
 * MERK at {@link ArkivMappenodeModell#barn} ikke nødvendigvis er i synk med tilstanden i datalager og må dermed brukes med dette i mente.
 * Det anbefales å erstatte bruk av typen {@link ArkivMappenodeModell} med {@link ArkivMappenodeDLM} */
@Injectable() //tilgjengeliggjøres via DatalagerModul
export class ArkivDatatjeneste {

    /** side rotmappen ikke har noen persistent id lager vi en 'fake' som kan benyttes ved operasjoner mot arkivLager */
    readonly syntetiskRotmappeId = 'rotmappe';

    /** Lokalt datalager for {@link ArkivNodeModell} entiteter. Bruker {@link ArkivNodeModell#forelder} som forelder.*/
      //public for testbarhet
    readonly arkivLager = createStore({name: 'arkiv'}, withEntities<ArkivNodeModell>());

    /** Holder rede på åpne subscriptions basert på arkivnode.id. Merk at vi her registrerer hvilke foreldre det finnes subscriptions for. */
    readonly arkivForelderAbonnenter = new DatalagerAbonnenter<ArkivnodeId>('arkiv.forelder'); //public for testbarhet

    /** Mapping fra gruppeId til grupperot-id (ArkivNode) */
    private readonly gruppeIdTilArkivRotId = new Map<GruppeId, ArkivnodeId>();

    constructor(private arkivRessurs: ArkivRessursClient,
                private favorittDatatjeneste: FavorittDatatjeneste,
                private meldingTilBruker: MeldingTilBrukerNg2,
                private oversettelser: Oversettelser,
                private modelloppdateringer: Modelloppdateringer) {

        this.lyttTilModelloppdateringer();
    }

    /** Laster angitt arkivnode fra server og oppdaterer lokalt datalager med denne. */
    async relastArkivFilnode(id: ArkivnodeId, feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Promise<void> {
        await firstValueFrom(serverFraRequest<ArkivFilnodeModell>({
            ressurskall: () => this.arkivRessurs.filNode(id).råRespons(),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, 'Kunne ikke hente biblioteksfil', 'relastArkivFilnode')
        })
          .pipe(tap(fil => registrerEntitetMedForelder(this.arkivLager, fil, this.forelderIdFraArkivnode(fil)))));
    }

    /** Returnerer arkivnode fra lokallager.
     * Forutsetter at arkivnode er lastet tidligere. */
    hentFraLokallager(id: ArkivnodeId): ArkivNodeModell {
        return hentFraLokallager(this.arkivLager, id);
    }

    /** Returnerer barn av angitt arkivnode, kun fra lokallager, uten å hente data fra server */
    hentBarnFraLokallager(forelderId: ArkivnodeId): Observable<ArkivNodeModell[]> {
        debug('hentBarnFraLokallager', forelderId, this.arkivForelderAbonnenter.antallAbonnenter(forelderId))
        return this.arkivForelderAbonnenter.observer(forelderId,
          this.arkivLager.pipe(
            selectManyByPredicate(forelderIdPredikat(forelderId)),
            sorterLevertSamling(sorterArkivnode),
            distinctUntilChangedDeep(), //kun lever verdier fra lageret ved reelle endringer
          ));
    }

    /** Erstatter alle arkivnoder knytte til angitt forelder i lokallager */
    erstattArkivnoderILokallager(arkivnoder: ArkivNodeModell[], forelderId: ArkivnodeId) {
        erstatteAlleEntiteterMedForelder(this.arkivLager, arkivnoder, forelderId);
    }

    /** Sletter alle arkivnoder knytte til angitt forelder i lokallager */
    slettArkivnoderTilknyttetForelder(forelderId: ArkivnodeId) {
        slettAlleEntiteterMedForelder(this.arkivLager, forelderId);
    }

    /** Laster rotmappe for aktiv bruker */
    rotmappeMedMeta(leveringsvalg?: Leveringsvalg.ikke_lever_ved_slettet, feilhåndteringsstrategi?: FeilhåndteringsstrategiForDataOgMeta): Observable<DataOgMeta<ArkivMappenodeModell>>;
    rotmappeMedMeta(leveringsvalg: Leveringsvalg, feilhåndteringsstrategi?: FeilhåndteringsstrategiForDataOgMeta): Observable<DataOgMeta<ArkivMappenodeModell | undefined>>;
    rotmappeMedMeta(leveringsvalg = Leveringsvalg.ikke_lever_ved_slettet, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Observable<DataOgMeta<ArkivMappenodeModell | undefined>> {
        return serverFraLagerOgRequestPakketIMeta({
            ressurskall: () => this.arkivRessurs.rot(),
            registrerILager: (respons: HttpResponse<ArkivMappenodeModell>) => {
                const rotnode = respons.body!;
                //siden rotnode ikke har noen id setter vi en syntetisk her
                rotnode.id = this.syntetiskRotmappeId;
                //oppdater info om hvordan vi går fra gruppeId til ArkivMappe
                rotnode.barn.forEach(gruppeRot => this.registrerGruppeIdTilGruppeRotMapping(gruppeRot));
                return registrerEntitetMedForelder(this.arkivLager, rotnode, this.forelderIdFraArkivnodeFn());
            },
            hentFraLager: () => this.arkivLager.pipe(
              selectEntity(this.syntetiskRotmappeId),
              map(mappe => mappe as ArkivMappenodeModell),
              leveringsvalg == Leveringsvalg.ikke_lever_ved_slettet ? filterNil() : utelatFørsteDersomNil(),
              distinctUntilChangedDeep()), //kun lever verdier fra lageret ved reelle endringer
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, 'Kunne ikke hente rotmappe', 'rotmappeMedMeta')
        });
    }

    /** Laster angitt sak.
     * Merk at dersom leveringsvalg = lever_undefined_ved_slettet vil returnert observable levere 'undefined' når forespurt entitet slettes i lageret */
    rotmappe(leveringsvalg?: Leveringsvalg.ikke_lever_ved_slettet, feilhåndteringsstrategi?: Feilhåndteringsstrategi): Observable<ArkivMappenodeModell>
    rotmappe(leveringsvalg: Leveringsvalg, feilhåndteringsstrategi?: Feilhåndteringsstrategi): Observable<ArkivMappenodeModell | undefined>
    rotmappe(leveringsvalg = Leveringsvalg.ikke_lever_ved_slettet, feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Observable<ArkivMappenodeModell | undefined> {
        return this.rotmappeMedMeta(leveringsvalg, somFeilhåndteringsstrategiForDataOgMeta(feilhåndteringsstrategi))
          .pipe(mapTilData());
    }

    rotmappeForGruppeMedMeta(gruppeId: GruppeId, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Observable<DataOgMeta<ArkivMappenodeModell>> {
        return serverFraLagerOgRequestPakketIMeta({
            ressurskall: () => this.arkivRessurs.rotForGruppe(gruppeId),
            registrerILager: (respons: HttpResponse<ArkivMappenodeModell>) => {
                const gruppeRot = respons.body!;
                this.registrerGruppeIdTilGruppeRotMapping(gruppeRot)
                return registrerEntitetMedForelder(this.arkivLager, gruppeRot, this.forelderIdFraArkivnodeFn());
            },
            hentFraLager: () =>
              this.arkivLager.pipe(
                selectEntity(this.gruppeIdTilArkivRotId.get(gruppeId)!),
                filterNil(), //tilfelle this.gruppeIdTilArkivRotId.get(gruppeId) returnerer undefined
                map(mappe => mappe as ArkivMappenodeModell),
                distinctUntilChangedDeep()), //kun lever verdier fra lageret ved reelle endringer
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, 'Kunne ikke hente mappe', 'rotmappeForGruppeMedMeta')
        });
    }

    rotmappeForGruppe(gruppeId: GruppeId, feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Observable<ArkivMappenodeModell> {
        return this.rotmappeForGruppeMedMeta(gruppeId, somFeilhåndteringsstrategiForDataOgMeta(feilhåndteringsstrategi))
          .pipe(mapTilData());
    }

    /** Laster angitt mappe.
     * Merk at dersom leveringsvalg = lever_undefined_ved_slettet vil returnert observable levere 'undefined' når forespurt entitet slettes i lageret */
    mappeMedMeta(id: ArkivnodeId, leveringsvalg?: Leveringsvalg.ikke_lever_ved_slettet, feilhåndteringsstrategi?: FeilhåndteringsstrategiForDataOgMeta): Observable<DataOgMeta<ArkivMappenodeModell>>;
    mappeMedMeta(id: ArkivnodeId, leveringsvalg: Leveringsvalg, feilhåndteringsstrategi?: FeilhåndteringsstrategiForDataOgMeta): Observable<DataOgMeta<ArkivMappenodeModell | undefined>>;
    mappeMedMeta(id: ArkivnodeId, leveringsvalg = Leveringsvalg.ikke_lever_ved_slettet, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Observable<DataOgMeta<ArkivMappenodeModell | undefined>> {
        return this.arkivForelderAbonnenter.observer(id,
          serverFraLagerOgRequestPakketIMeta({
              ressurskall: () => this.arkivRessurs.mappe(id),
              registrerILager: (respons: HttpResponse<ArkivMappenodeModell>) => this.oppdaterMappeOgBarnILokallager(respons.body!),
              hentFraLager: () => this.arkivLager.pipe(
                selectEntity(id),
                map(mappe => mappe as ArkivMappenodeModell),
                leveringsvalg == Leveringsvalg.ikke_lever_ved_slettet ? filterNil() : utelatFørsteDersomNil(),
                distinctUntilChangedDeep()), //kun lever verdier fra lageret ved reelle endringer
              ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, 'Kunne ikke hente arkivmappe', 'mappeMedMeta')
          }));
    }

    /** Laster angitt mappe.
     * Merk at dersom leveringsvalg = lever_undefined_ved_slettet vil returnert observable levere 'undefined' når forespurt entitet slettes i lageret */
    mappe(id: ArkivnodeId, leveringsvalg?: Leveringsvalg.ikke_lever_ved_slettet, feilhåndteringsstrategi?: Feilhåndteringsstrategi): Observable<ArkivMappenodeModell>
    mappe(id: ArkivnodeId, leveringsvalg: Leveringsvalg, feilhåndteringsstrategi?: Feilhåndteringsstrategi): Observable<ArkivMappenodeModell | undefined>
    mappe(id: ArkivnodeId, leveringsvalg = Leveringsvalg.ikke_lever_ved_slettet, feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Observable<ArkivMappenodeModell | undefined> {
        return this.mappeMedMeta(id, leveringsvalg, somFeilhåndteringsstrategiForDataOgMeta(feilhåndteringsstrategi))
          .pipe(mapTilData());
    }

    barnMedMeta(forelderId: ArkivnodeId, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Observable<DataOgMeta<ArkivNodeModell[]>> {
        return this.arkivForelderAbonnenter.observer(forelderId,
          serverFraLagerOgRequestPakketIMeta({
              ressurskall: () => this.arkivRessurs.mappe(forelderId),
              registrerILager: (respons: HttpResponse<ArkivMappenodeModell>) => this.oppdaterMappeOgBarnILokallager(respons.body!).barn,
              hentFraLager: () => this.arkivLager.pipe(
                selectManyByPredicate(forelderIdPredikat(forelderId)),
                sorterLevertSamling(sorterArkivnode),
                distinctUntilChangedDeep()), //kun lever verdier fra lageret ved reelle endringer
              ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, 'Kunne ikke hente mappeinnhold', 'barn')
          }));
    }

    barn(forelderId: ArkivnodeId, feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Observable<ArkivNodeModell[]> {
        return this.barnMedMeta(forelderId, somFeilhåndteringsstrategiForDataOgMeta(feilhåndteringsstrategi))
          .pipe(mapTilData());
    }

    /** Markerer angitt dokument som lest i lokalt datalagere, dersom dokumentet finnes i lageret */
    markerSomLestILokallager(dokumentId: DokumentId) {
        const filnode = this.finnArkivFilnode(dokumentId);
        if (erNil(filnode)) return;
        this.oppdaterILokallager(filnode.id, {dokument: {...filnode.dokument, erLestAv: true}});
    }

    async oppdaterMappe(mappeId: ArkivnodeId, spesifikasjon: OppdaterMappeSpesifikasjon, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Promise<DataOgMeta<ArkivMappenodeDLM>> {
        return firstValueFrom(serverFraRequestPakketIMeta({
            ressurskall: () => this.arkivRessurs.oppdaterMappe(mappeId, spesifikasjon).råRespons()
              .pipe(tap(respons => oppdaterEntitetMedForelder(this.arkivLager, respons.body!, this.forelderIdFraArkivnode(respons.body!)))),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke oppdatere arkivmappe')), 'oppdaterMappe')
        }));
    }

    async opprettMappe(forelderId: ArkivnodeId, spesifikasjon: NyMappeSpesifikasjon, feilhåndteringsstrategi = stdFeilhåndteringsstrategiForDataOgMeta): Promise<DataOgMeta<ArkivMappenodeDLM>> {
        return firstValueFrom(serverFraRequestPakketIMeta({
            ressurskall: () => this.arkivRessurs.opprettMappe(forelderId, spesifikasjon).råRespons()
              .pipe(tap(respons => registrerEntitetMedForelder(this.arkivLager, respons.body!, this.forelderIdFraArkivnode(respons.body!)))),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke opprette ny arkivmappe')), 'opprettMappe')
        }));
    }

    opprettArkivdokument(mappeId: ArkivnodeId, fil: File, feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Observable<HttpEvent<ArkivFilnodeModell>> {
        return this.arkivRessurs.opprettFil(mappeId, formDataMedFil(fil), {rapporterProgresjon: true}).råEventer()
          .pipe(
            this.registrerNyttArkivdokumentMedProgresjonseventer(),
            håndterRequestfeilViaCatchError(this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke registrere nytt arkivdokument')), 'opprettArkivdokument'))
          );
    }

    opprettArkivdokumentVersjon(filId: ArkivnodeId, fil: File, feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Observable<HttpEvent<ArkivFilnodeModell>> {
        return this.arkivRessurs.opprettNyFilVersjon(filId, formDataMedFil(fil), {rapporterProgresjon: true}).råEventer()
          .pipe(
            this.registrerNyttArkivdokumentMedProgresjonseventer(),
            håndterRequestfeilViaCatchError(this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke registrere nytt arkivdokument')), 'opprettArkivdokument'))
          );
    }

    importer(mappeId: ArkivnodeId, zipfil: File, feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Observable<HttpEvent<ImportresultatModell>> {
        return this.arkivRessurs.importer(mappeId, formDataMedFil(zipfil), {rapporterProgresjon: true}).råEventer()
          .pipe(
            tap(event => {
                if (event.type == HttpEventType.Response) {
                    console.log('#IMPORTERT', event.body);
                    this.oppdaterMappeOgBarnILokallager(event.body!.rotmappe)
                }
            }),
            håndterRequestfeilViaCatchError(this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke importere zip-fil til biblioteket')), 'importer'))
          );
    }

    async flytt(nodeId: ArkivnodeId, målmappeId: ArkivnodeId, feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Promise<ArkivMappenodeDLM> {
        return firstValueFrom(serverFraRequest({
            ressurskall: () => this.arkivRessurs.flytt(nodeId, {målmappe: målmappeId}).råRespons()
              .pipe(tap(respons => {
                  emitOnce(async () => {
                      //ta vare på ref til nodeSomFlyttes da denne vil slettes når vi oppdaterer lager med info om mappen noden har blitt flyttet fra
                      const nodeSomFlyttes = this.hentFraLokallager(nodeId);

                      //oppdater lager med info om mappen noden har blitt flyttet fra
                      this.oppdaterMappeOgBarnILokallager(respons.body! as ArkivMappenodeModell);

                      //flytt node til målmappe
                      nodeSomFlyttes.forelder = this.hentFraLokallager(målmappeId) as ArkivMappenodeModell;
                      registrerEntitetMedForelder(this.arkivLager, nodeSomFlyttes, målmappeId); //nodeSomFlyttes har blitt fjernet fra lager av this.oppdaterMappeOgBarnILokallager(..)
                  });
              })),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke flytte elementet')), 'flytt')
        }));
    }

    /** Oppdaterer angitt arkivnode i lokallager med verdier angitt i `oppdatering`. */
    oppdaterILokallager(arkivnodeId: ArkivnodeId, oppdatering: Partial<ArkivMappenodeDLM | ArkivFilnodeModell>) {
        this.arkivLager.update(updateEntities(arkivnodeId, oppdatering));
    }

    /** Oppdaterer angitt arkivmappenode i lokallager med angitt skjermingsinformasjon. */
    oppdaterSkjermingAvMappeILokallager(arkivnodeId: ArkivnodeId, skjerming: SkjermingModell) {
        const node = hentFraLokallagerEllerNull(this.arkivLager, arkivnodeId);
        if (erNil(node)) return;
        verifiserErSann(erMappeNode(node), `angitt node:${arkivnodeId} er ikke en mappenode`);
        this.oppdaterILokallager(arkivnodeId, {skjerming: skjerming});
    }

    /** Oppdaterer angitt dokumentId i lokallager med angitt skjermings-informasjon. */
    oppdaterSkjermingAvFilILokallager(dokumentId: DokumentId, skjerming: SkjermingModell) {
        const node = this.finnArkivFilnode(dokumentId);
        if (erNil(node)) return;
        this.oppdaterILokallager(node.id, {dokument: {...node.dokument, skjerming: skjerming}});
    }

    oppdaterArkivFilnodeILokallager(arkivnodeId: ArkivnodeId, oppdateringsmetode: (node: ArkivFilnodeModell) => ArkivFilnodeModell) {
        this.arkivLager.update(updateEntities(arkivnodeId, node => oppdateringsmetode(node as ArkivFilnodeModell)));
    }

    /** Registrerer dokument som brukers favoritt dersom ikke allerede markert som favoritt,
     * eller fjernes angitt dokument som brukers favoritt  */
    async endreFavorittmarkering(dokumentversjon: DokumentVersjonModell): Promise<void> {
        const node = this.finnArkivFilnode(dokumentversjon.dokumentId);
        if (erNil(node)) return;

        const favoritterOppdatert = await this.favorittDatatjeneste.endreFavorittmarkering({id: dokumentversjon.dokumentId, favoritter: dokumentversjon.favoritter}, FavorittType.dokument);
        this.oppdaterILokallager(node.id, {dokument: {...node.dokument, favoritter: favoritterOppdatert}});
    }

    /** Sletter angitt dokument */
    slettDokument(arkivFilnodeId: ArkivnodeId, feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Promise<any> {
        return firstValueFrom(serverFraRequest({
            ressurskall: () => this.arkivRessurs.slettFil(arkivFilnodeId).råRespons()
              .pipe(tap(respons => this.oppdaterMappeOgBarnILokallager(respons.body!))),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke slette arkivdokument')), 'slettDokument')
        }));
    }

    /** Sletter angitt mappe, rekursivt dersom angitt */
    slettMappe(mappeId: ArkivnodeId, spesifikasjon: { rekursivt: boolean }, feilhåndteringsstrategi = stdFeilhåndteringsstrategi): Promise<ArkivMappenodeDLM> {
        return firstValueFrom(serverFraRequest({
            ressurskall: () => this.arkivRessurs.slettMappe(mappeId, spesifikasjon).råRespons()
              .pipe(tap(respons => {
                  this.oppdaterMappeOgBarnILokallager(respons.body! as ArkivMappenodeModell);
              })),
            ...this.feilhåndteringArgumenter(feilhåndteringsstrategi, this.oversettelser.oversett(i18n('Kunne ikke slette arkivmappe')), 'slettMappe')
        }));
    }

    //hjelpemetoder

    private registrerGruppeIdTilGruppeRotMapping(gruppeRot: ArkivNodeModell) {
        this.gruppeIdTilArkivRotId.set(gruppeRot.eiendeGruppeId, gruppeRot.id);
    }

    /** Registrerer (oppdaterer) angitt mappe og dens barn,
     *  sørger for at barn i lager som ikke er med i angitt mappes barn slettes rekursivt */
    private oppdaterMappeOgBarnILokallager(mappeMedBarn: ArkivMappenodeModell): ArkivMappenodeModell {
        return emitOnce(() => {
            oppdaterEntitetMedForelder(this.arkivLager, mappeMedBarn, this.forelderIdFraArkivnode(mappeMedBarn));
            //finn eksisterende barn - for å kunne slette de som ikke lenger er med i mappeMedBarn
            const eksisterendeBarn = finnFraLokallagerBasertPåForelderId(this.arkivLager, mappeMedBarn.id);
            let slettedeBarn = eksisterendeBarn
              .filter(eksisterende => !inneholderEntitetBasertPåId(mappeMedBarn.barn, eksisterende))
              .map(foreldreløs => slettRekursivt(this.arkivLager, foreldreløs.id))
              .flat();
            //legg barna til i lager
            registrerEntiteterMedForelder(this.arkivLager, mappeMedBarn.barn, mappeMedBarn.id);
            debug(`oppdaterMappeOgBarnILokallager, registrert mappe ${mappeMedBarn.id} (${mappeMedBarn.barn.length} barn), slettet totalt ${slettedeBarn.length} foreldreløse barn`, mappeMedBarn, slettedeBarn);

            return mappeMedBarn;
        });
    }

    //public for testbarhet
    forelderIdFraArkivnode(arkivnode: ArkivNodeModell): ArkivnodeId {
        if (arkivnode.erRotMappe) return this.syntetiskRotmappeId;
        return arkivnode.forelder!.id;
    }

    forelderIdFraArkivnodeFn(): ArkivDatatjeneste['forelderIdFraArkivnode'] {
        return arkivnode => this.forelderIdFraArkivnode(arkivnode);
    }

    /** Finner ArkivFilnodeModell i lokallageret som representerer angitt dokumentId, null dersom ikke funnet */
    private finnArkivFilnode(dokumentId: string): ArkivFilnodeModell | null {
        return this.arkivLager.query(getEntityByPredicate(node => erFilNode(node) && node.dokument.dokumentId == dokumentId)) as ArkivFilnodeModell ?? null;
    }

    private feilhåndteringArgumenter<RET_TYPE, STRATEGI extends STRATEGI_TYPE>(
      feilhåndteringsstrategi: STRATEGI,
      brukerrettetFeilmelding: string,
      metodeNavnForDebug: string): FeilhåndteringArgumenter<RET_TYPE, STRATEGI> {
        return {
            feilhåndteringsstrategi,
            visFeilmelding: visFeilmeldingVhjaMeldingTilBruker(brukerrettetFeilmelding, this.meldingTilBruker),
            dataReturnertVedFeil: null,
            debug: (formatter: any, ...args: any[]): void => debug(`${metodeNavnForDebug}: ${formatter}`, args),
        }
    }

    private registrerNyttArkivdokumentMedProgresjonseventer(): MonoTypeOperatorFunction<HttpEvent<ArkivFilnodeModell>> {
        return tap<HttpEvent<ArkivFilnodeModell>>(event => {
            if (event.type == HttpEventType.Response) {
                const nyttArkivdokument = event.body!;
                registrerEntitetMedForelder(this.arkivLager, nyttArkivdokument, this.forelderIdFraArkivnode(nyttArkivdokument));
            }
        });
    }

    /**
     * Lytt til relevante modelloppdateringer via {@link Modelloppdateringer} (push-baserte oppdateringer).
     * Merk at vi aldri avregistrerer subscription vi oppretter her da data-tjenesten aldri termineres.
     */
    private lyttTilModelloppdateringer() {
        this.lyttTilNyArkivnode();
        this.lyttTilArkivnodeOppdatert();
        this.lyttTilArkivnodeSlettet();
        this.lyttTilDokumentKonvertertTilPdf();
        this.lyttTilForsidebildeOpprettet();
        this.lyttTilDokumentTilSignering();
        this.lyttTilDokumentKomplettSignert();
        this.lyttTilDokumentSigneringKanselert();
    }

    private lyttTilNyArkivnode() {
        this.opprettetEllerOppdatertArkivnodeHendelse<OppdateringmodellArkivnode>(ModelloppdateringType.NY_ARKIVNODE)
          .medAbonnentoppslag(this.arkivForelderAbonnenter, hendelseModell => hendelseModell.arkivnodeId)
          .medAbonnentoppslagOpt(this.arkivForelderAbonnenter, hendelseModell => hendelseModell.forelderId)
          .medEtterLagerOppdatert(oppdatering => this.visMelding(i18n('Ny arkivnode "{arkivnode}" registrert (av {bruker})'), this.i18nArgumenter(oppdatering, oppdatering.modell.arkivnodeId)))
          .oppdaterLagerVedHendelse();
    }

    private lyttTilArkivnodeOppdatert() {
        this.opprettetEllerOppdatertArkivnodeHendelse<OppdateringmodellArkivnode>(ModelloppdateringType.ARKIVNODE_OPPDATERT)
          .medAbonnentoppslag(this.arkivForelderAbonnenter, hendelseModell => hendelseModell.arkivnodeId)
          .medAbonnentoppslagOpt(this.arkivForelderAbonnenter, hendelseModell => hendelseModell.forelderId)
          .medEtterLagerOppdatert(oppdatering => this.visMelding(i18n('Arkivnode "{arkivnode}" oppdatert (av {bruker})'), this.i18nArgumenter(oppdatering, oppdatering.modell.arkivnodeId)))
          .oppdaterLagerVedHendelse();
    }

    private lyttTilDokumentKonvertertTilPdf() {
        this.oppdatertArkivfilHendelse(ModelloppdateringType.DOKUMENT_KONVERTERTPDF).oppdaterLagerVedHendelse();
    }

    private lyttTilForsidebildeOpprettet() {
        this.oppdatertArkivfilHendelse(ModelloppdateringType.DOKUMENT_FORSIDEBILDEOPPRETTET).oppdaterLagerVedHendelse();
    }

    private lyttTilDokumentTilSignering() {
        this.oppdatertArkivfilHendelse(ModelloppdateringType.DOKUMENT_TILSIGNERING).oppdaterLagerVedHendelse();
    }

    private lyttTilDokumentKomplettSignert() {
        this.oppdatertArkivfilHendelse(ModelloppdateringType.DOKUMENT_KOMPLETTSIGNERT).oppdaterLagerVedHendelse();
    }

    private lyttTilDokumentSigneringKanselert() {
        this.oppdatertArkivfilHendelse(ModelloppdateringType.DOKUMENT_LÅSTOPP).oppdaterLagerVedHendelse();
    }

    private lyttTilArkivnodeSlettet() {
        this.slettetHendelse<OppdateringmodellArkivnodeSlettet>(ModelloppdateringType.ARKIVNODE_SLETTET)
          .medFørLagerOppdatert(oppdatering => this.visMelding(i18n('Arkivnode "{arkivnode}" har blitt slettet (av {bruker})'), this.i18nArgumenter(oppdatering, oppdatering.modell.arkivnodeId)))
          .oppdaterLagerVedHendelse();
    }

    private slettetHendelse<HENDELSE_MODELL_TYPE extends OppdateringmodellArkivnodeSlettet>(hendelseType: ModelloppdateringType) {
        return new ModellSlettetHendelseHåndteringBygger<HENDELSE_MODELL_TYPE, ArkivNodeModell>(this.arkivLager, this.modelloppdateringer)
          .lyttTil(hendelseType)
          .medEntitetId(hendelseModell => hendelseModell.arkivnodeId);
    }

    private opprettetEllerOppdatertArkivnodeHendelse<HENDELSE_MODELL_TYPE extends OppdateringmodellArkivnode>(hendelseType: ModelloppdateringType) {
        return new ModellOpprettetEllerOppdatertHendelseHåndteringBygger<HENDELSE_MODELL_TYPE, ArkivNodeModell>(this.arkivLager, this.modelloppdateringer)
          .lyttTil(hendelseType)
          .medEntitetId(hendelseModell => hendelseModell.arkivnodeId)
          .medEntitetLaster(hendelseModell => hendelseModell.mappe ?
            this.arkivRessurs.mappe(hendelseModell.arkivnodeId).råRespons() :
            this.arkivRessurs.filNode(hendelseModell.arkivnodeId).råRespons())
          .medForberedLageroppdatering(arkivnode => leggTilForelderId(arkivnode, this.forelderIdFraArkivnode(arkivnode)));
    }

    private oppdatertArkivfilHendelse<HENDELSE_MODELL_TYPE extends (OppdateringmodellDokumentversjon | OppdateringmodellDokument)>(hendelseType: ModelloppdateringType) {
        return new ModellOpprettetEllerOppdatertHendelseHåndteringBygger<HENDELSE_MODELL_TYPE, ArkivNodeModell>(this.arkivLager, this.modelloppdateringer)
          .lyttTil(hendelseType)
          .medEntitetId(hendelseModell => hendelseModell.arkivfilId!)
          .medEntitetLaster(hendelseModell => this.arkivRessurs.filNode(hendelseModell.arkivfilId!).råRespons())
          .medForberedLageroppdatering(arkivnode => leggTilForelderId(arkivnode, this.forelderIdFraArkivnode(arkivnode)))
          .medAbonnentoppslag(this.arkivForelderAbonnenter, hendelseModell => hendelseModell.arkivmappeId!)
    }

    /** returnerer objekt med 'arkivnode' og 'bruker' felt populert fra angitt oppdatering  */
    private i18nArgumenter(oppdatering: Modelloppdatering<any>, arkivnodeId: ArkivnodeId) {
        return i18nArgumenterMedAnsvarligBruker(oppdatering, {arkivnode: this.arkivnodeNavn(arkivnodeId)});
    }

    /** Returnerer arkivnode.filnavn for arkivnode registrert i arkivnodeLager, '...' dersom arkiven ikke finnes i arkivLager */
    private arkivnodeNavn(arkivnodeId: ArkivnodeId) {
        return this.hentFraLokallager(arkivnodeId)?.navn ?? '...';
    }

    private visMelding(melding: string, argumenter = {}) {
        this.meldingTilBruker.infomelding(this.oversettelser.oversett(melding, argumenter));
    }
}

/** Datalager-spesifikk representasjon av en arkivmappe der {@link ArkivMappenodeModell#barn} er fjernet.
 * Dette da datalageret ikke garanterer at mappe.barn listen er oppdatert.
 * Barn må hentes via eksplisitte metoder for dette, feks {@link #barn} / {@link #hentBarnFraLokallager}. */
export type ArkivMappenodeDLM = Omit<ArkivMappenodeModell, 'barn'>;
